import React, { useState } from "react";
import { Navbar, Footer } from "@/components/yc/Nav";
import { HealthDial, SubScoreBar } from "@/components/yc/HealthDial";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";

const HealthAudit = () => {
    const [form, setForm] = useState({
        email: "", company_name: "", sector: "",
        posts_per_month: 4, has_brand_guidelines: false,
        audience_clarity: 5, last_post_days_ago: 20,
    });
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true); setError("");
        try {
            const res = await api.post("/public/health-audit", form);
            setResult(res.data);
        } catch (err) {
            setError(err.response?.data?.detail || "Could not run audit");
        } finally { setLoading(false); }
    };

    return (
        <div className="min-h-screen bg-yc-cream">
            <Navbar />
            <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
                <div className="text-center mb-12">
                    <div className="yc-overline">For Brands</div>
                    <h1 className="font-heading text-5xl md:text-6xl font-black tracking-tighter mt-3">Curated Health Score audit</h1>
                    <p className="text-lg text-yc-ink/80 mt-4 max-w-2xl mx-auto">Get an indicative confidence score in under a minute. The full, ongoing Health Score is exclusive to Corporate clients.</p>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <form onSubmit={submit} data-testid="audit-form" className="yc-card p-8 space-y-5">
                        <div>
                            <label className="yc-overline">Work email</label>
                            <input data-testid="audit-email" required type="email" className="yc-input mt-2" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                        </div>
                        <div>
                            <label className="yc-overline">Company name</label>
                            <input data-testid="audit-company" required className="yc-input mt-2" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
                        </div>
                        <div>
                            <label className="yc-overline">Sector</label>
                            <input data-testid="audit-sector" required className="yc-input mt-2" placeholder="Fashion, SaaS, Beauty…" value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="yc-overline">Posts / month</label>
                                <input data-testid="audit-posts" type="number" min="0" className="yc-input mt-2" value={form.posts_per_month} onChange={(e) => setForm({ ...form, posts_per_month: parseInt(e.target.value || 0) })} />
                            </div>
                            <div>
                                <label className="yc-overline">Days since last post</label>
                                <input data-testid="audit-days" type="number" min="0" className="yc-input mt-2" value={form.last_post_days_ago} onChange={(e) => setForm({ ...form, last_post_days_ago: parseInt(e.target.value || 0) })} />
                            </div>
                        </div>
                        <div>
                            <label className="yc-overline">Audience clarity (1-10)</label>
                            <input data-testid="audit-clarity" type="range" min="1" max="10" className="w-full mt-2" value={form.audience_clarity} onChange={(e) => setForm({ ...form, audience_clarity: parseInt(e.target.value) })} />
                            <div className="text-sm">{form.audience_clarity}/10</div>
                        </div>
                        <label className="flex items-center gap-2">
                            <input data-testid="audit-guidelines" type="checkbox" className="w-5 h-5" checked={form.has_brand_guidelines} onChange={(e) => setForm({ ...form, has_brand_guidelines: e.target.checked })} />
                            <span className="text-sm">We have documented brand guidelines</span>
                        </label>
                        {error && <div className="text-yc-pink text-sm">{error}</div>}
                        <button data-testid="audit-submit" disabled={loading} className="yc-btn-primary w-full">{loading ? "Calculating…" : "Get my Health Score"}</button>
                    </form>

                    <div className="yc-card p-8">
                        {result ? (
                            <div data-testid="audit-result">
                                <div className="flex flex-col items-center">
                                    <HealthDial score={result.overall} label="Indicative Score" />
                                </div>
                                <div className="mt-8 grid grid-cols-1 gap-3">
                                    {["consistency", "performance", "audience_fit", "brand_safety", "freshness"].map((k) => (
                                        <SubScoreBar key={k} label={k} value={result[k]} hint={result.guidance[k]} />
                                    ))}
                                </div>
                                <div className="mt-8 text-center">
                                    <Link to="/brands/join" className="yc-btn-accent" data-testid="audit-join-cta">Join Curated Chaos to fix this →</Link>
                                </div>
                            </div>
                        ) : (
                            <div className="h-full flex flex-col items-center justify-center text-center min-h-[400px]">
                                <div className="font-heading text-2xl font-bold tracking-tight">Your score appears here.</div>
                                <p className="text-yc-ink/70 mt-3 max-w-sm">Fill in the form. We compute five sub-scores and one composite confidence index, with guidance on improving each.</p>
                            </div>
                        )}
                    </div>
                </div>
            </section>
            <Footer />
        </div>
    );
};

export default HealthAudit;
